import {inject, Injectable, NgZone} from '@angular/core';
import {Platform} from "@ionic/angular/standalone";
import {Capacitor} from "@capacitor/core";
import {ErrorService} from "../error/error.service";
import {
  BehaviorSubject,
  combineLatest,
  Observable,
  pairwise,
  timer
} from "rxjs";
import {AuthService} from "../auth/auth.service";
import {LoggedInUser} from "../auth/logged-in-user.model";
import {SpinnerService} from "../spinner/spinner.service";
import {LoggerService} from "../logging/logger.service";
import {
  ObservabilityLevel,
  ObservabilityService
} from "../observability/observability.service";
import {distinctUntilChanged, filter, map} from "rxjs/operators";
import {NavigationEnd, Router} from "@angular/router";
import {
  CustomerInfo,
  PURCHASE_PROVIDER,
  PurchaseProvider,
  SubscriptionPackage,
  SubscriptionProduct
} from './purchase-provider.interface';
import {UserContextService} from
  '../userContext/user-context.service';
import {PostHogService} from
  '../posthog/posthog.service';
import {
  ONBOARDING_ACTIVITY_PATH
} from '../../onboarding/onboarding-state.service';

/**
 * One access reading (HEC-346): resolved access, and what
 * RevenueCat has said so far (null until it answers). Built by
 * accessReadings$, the single source the paywall navigation
 * and hasAccess$ both read (HEC-377).
 */
interface AccessReading {
  access: boolean | null;
  sub: boolean | null;
}

@Injectable({
  providedIn: 'root'
})
export class SubscriptionService {
  private readonly platform = inject(Platform);
  private readonly errorService = inject(ErrorService);
  private readonly authService = inject(AuthService);
  private readonly spinnerService =
    inject(SpinnerService);
  private readonly logger = inject(LoggerService);
  private readonly observabilityService =
    inject(ObservabilityService);
  private readonly router = inject(Router);
  private readonly zone = inject(NgZone);
  private readonly provider: PurchaseProvider =
    inject(PURCHASE_PROVIDER);
  private readonly userContextService =
    inject(UserContextService);
  private readonly postHogService =
    inject(PostHogService);

  /**
   * How long Home holds its loading state for RevenueCat's
   * answer before falling back to the paywall. Matches
   * HomeService.LOADING_TIMEOUT_MS, the page-level gate
   * convention.
   */
  private static readonly ACCESS_UNKNOWN_TIMEOUT_MS = 5000;

  private customerInfo?: CustomerInfo;
  private listenerRegistered = false;

  /**
   * A genuine in-session true -> false transition not yet
   * acted on (HEC-376). A stale RevenueCat cache read at
   * cold start never sets it, so it can never start the
   * unsubscribed role poll for a paying user.
   */
  private unsubscribePollPending = false;

  /**
   * What this session's last customerInfo said. Kept apart
   * from isSubscribedSubject, which outlives a logout, so
   * the next user's first update is not read as a lapse.
   */
  private lastCustomerInfoSubscribed = false;

  /**
   * What RevenueCat has actually told us, and nothing else:
   * null until the first customerInfo lands (HEC-359). "Not
   * yet known" used to share the value false with "not
   * subscribed", so Home's paywall mounted on every cold
   * start before the SDK had answered.
   */
  private readonly isSubscribedSubject =
    new BehaviorSubject<boolean | null>(null);
  public isSubscribed$: Observable<boolean | null> =
    this.isSubscribedSubject.asObservable();

  /**
   * Bounds the unknown state: set once it has outlived
   * ACCESS_UNKNOWN_TIMEOUT_MS, so a customerInfo callback
   * that never fires degrades to the paywall rather than a
   * permanent spinner. Kept apart from isSubscribedSubject
   * so a resolved "not subscribed" stays distinguishable
   * from "gave up waiting" - the paywall_presented capture
   * relies on that. Declared above accessReadings$ because
   * field initialisers run in declaration order.
   */
  private readonly accessTimedOutSubject =
    new BehaviorSubject<boolean>(false);

  /**
   * Read-only view of the timed-out flag (HEC-377), for the
   * paywall to show its unavailable card rather than an empty
   * plan picker when it is mounted off the timeout with the
   * offerings still loading.
   */
  public readonly accessTimedOut$: Observable<boolean> =
    this.accessTimedOutSubject.asObservable();

  /**
   * One source for every access reading (HEC-377): resolved
   * access alongside what RevenueCat has said so far. The
   * paywall navigation's pairwise() reads this stream, not a
   * combineLatest of hasAccess$ and isSubscribed$: that pair
   * emits a transient {false,null} as RevenueCat settles, so a
   * role-granted subscriber whose answer lands as "not
   * subscribed" would navigate twice. Deduped on both fields,
   * so a reading only repeats when something changed.
   */
  private readonly accessReadings$: Observable<AccessReading> =
    combineLatest([
      this.isSubscribed$,
      this.userContextService.currentUser$,
      this.accessTimedOutSubject
    ]).pipe(
      map(([sub, , timedOut]): AccessReading => ({
        access: this.resolveAccess(sub, timedOut),
        sub
      })),
      distinctUntilChanged((a, b) =>
        a.access === b.access && a.sub === b.sub)
    );

  /**
   * true or false once access is known, null while it is
   * not. Role-granted access is a synchronous token check, so
   * a trial user, or a subscriber whose token carries
   * event.access (HEC-377), never sees null.
   */
  public hasAccess$: Observable<boolean | null> =
    this.accessReadings$.pipe(
      map(reading => reading.access),
      distinctUntilChanged()
    );

  /** Plain boolean: unknown reads as not subscribed. */
  public get isSubscribed(): boolean {
    return this.isSubscribedSubject.getValue() === true;
  }

  private readonly initialisedSubject =
    new BehaviorSubject<boolean>(false);
  public initialised$ =
    this.initialisedSubject.asObservable();

  private readonly offeringsSubject =
    new BehaviorSubject<SubscriptionPackage[] | null>(
      null
    );
  public offerings$ =
    this.offeringsSubject.asObservable();

  loggedInUser: LoggedInUser | null = null;

  /**
   * Routes a customerInfo update must never pull the user away
   * from (HEC-346): the landing flow (today's rule), and the
   * activity forms, where a navigation would discard what they
   * are typing. A route check rather than EventDraftService's
   * draft state (which only turns non-none once the user types,
   * so a resumed-but-untouched draft would be missed) or the
   * HEC-178 confirmDiscardEventForm guard (which deliberately
   * lets a tab switch park a dirty form rather than blocking it).
   */
  private static readonly BUSY_ROUTE_PREFIXES = [
    '/landing',
    '/tabs/events/add',
    '/tabs/events/edit',
    ONBOARDING_ACTIVITY_PATH
  ];

  /**
   * Set when a paywall navigation was suppressed on a busy
   * route (HEC-346). The suppression protects what the user
   * is typing, not their access: once they leave that route
   * with access still lost, the navigation they were owed
   * runs, so a lapsed user who cancels the form is not left
   * on a tab page with no tab bar and no data. Cleared when
   * access comes back first, and on logout.
   */
  private paywallDeferred = false;

  constructor() {
    // UserContextService keeps its boolean contract:
    // unknown is forwarded as not subscribed, with a
    // separate flag saying whether RevenueCat has answered.
    this.isSubscribed$
      .pipe(distinctUntilChanged())
      .subscribe(value => {
        this.userContextService
          .setSubscriptionKnown(value !== null);
        this.userContextService
          .setSubscribed(value === true);
      });

    // HEC-346: one edge-triggered subscription replaces the
    // customerInfo listener's navigate-on-every-unsubscribed
    // update. It covers the RevenueCat unsubscribe, the cold
    // start of a lapsed user off Home and a trial lost on a
    // token refresh alike. Fed from accessReadings$ (HEC-377),
    // one source, so no transient reading sits between two
    // genuine ones.
    this.accessReadings$
      .pipe(pairwise())
      .subscribe(([previous, current]) => {
        if (current.access === true) {
          this.paywallDeferred = false;
        }
        if (SubscriptionService
          .shouldNavigateToPaywall(previous, current)) {
          this.navigateToPaywallUnlessBusy();
        }
      });

    // The deferred half of the busy-route rule: run the
    // suppressed navigation once the user leaves that route.
    this.router.events
      .pipe(filter((event): event is NavigationEnd =>
        event instanceof NavigationEnd))
      .subscribe(event => this.runDeferredPaywallNavigation(
        event.urlAfterRedirects));

    combineLatest([
      this.authService.initialised$,
      this.authService.loggedInUser$,
    ]).pipe(
      map(async ([authInitialised, user]) => {
        // only proceed if auth is initialised
        if (authInitialised) {
          // then we know this is the good state of
          // the user...
          if (user && !this.loggedInUser) {
            this.loggedInUser = user;
            this.logger.info(
              'SubscriptionService detected '
              + 'logged in user:',
              user.getSub()
            );
            this.initialiseUser(
              this.loggedInUser.getSub()
            ).then();
          } else if (this.loggedInUser && !user) {
            this.logger.info(
              'User logged out, resetting '
              + 'SubscriptionService state'
            );
            this.loggedInUser = null;
            // HEC-359: without this, a stale timed-out
            // flag from the previous session collapses
            // the next login's hasAccess$ straight to
            // false before its own grace window starts.
            this.accessTimedOutSubject.next(false);
            // HEC-376: a pending lapse belongs to this
            // user and must not carry over to the next.
            this.unsubscribePollPending = false;
            this.lastCustomerInfoSubscribed = false;
          } else {
            // we're not logged in, so mark as
            // initialised
            this.logger.info(
              'no change, just marking '
              + 'SubscriptionService as initialised'
            );
            this.initialisedSubject.next(true);
          }
        }
    })).subscribe();
  }

  public async initialiseUser(
    appUserId: string,
    forceLoadOfferings: boolean = false
  ) {
    this.logger.info(
      'Platform is', Capacitor.getPlatform(),
      'Initializing Purchases with appUserId:',
      appUserId
    );

    await this.platform.ready();
    // Armed only once the platform is ready, so a slow
    // native start-up cannot eat the budget and push a
    // paying subscriber back onto the paywall.
    this.startAccessUnknownTimer();

    try {
      if (!this.isCurrentPlatformSupported()) {
        this.zone.run(
          () => this.isSubscribedSubject.next(true)
        );
        this.logger.info(
          'Platform not supported, '
          + 'auto-subscribing'
        );
        this.observabilityService
          .trackServiceInitialized(
            'SubscriptionService',
            {
              platform: 'web',
              isSubscribed: this.isSubscribed,
              platformSupported: false
            }
          );
        this.initialisedSubject.next(true);
        return;
      }

      // Check if already configured
      const isConfigured =
        await this.provider.isConfigured();

      if (isConfigured) {
        this.logger.debug(
          'Purchases already configured, '
          + 'calling login with appUserId: '
          + appUserId
        );
        await this.provider.logIn(appUserId);
        this.provider.initialiseAttribution();
        await this.addCustomerInfoListener();
        await this.loadOfferings();
        this.initialisedSubject.next(true);
      } else {
        // Configure Purchases for the first time
        await this.configurePurchases(appUserId);
      }

      if (forceLoadOfferings) {
        // Force a reload of the offerings
        await this.loadOfferings();
      }

      this.observabilityService
        .trackServiceInitialized(
          'SubscriptionService',
          {
            platform: Capacitor.getPlatform(),
            appUserId: appUserId,
            isSubscribed: this.isSubscribed,
            platformSupported: true
          }
        );

    } catch (error) {
      this.logger.error(
        'Error during initialization:', error
      );
      this.observabilityService.captureException(
        error,
        {
          context:
            'subscription_service_initialization',
          platform: Capacitor.getPlatform(),
          appUserId: appUserId ?? 'anonymous',
          isCurrentPlatformSupported:
            this.isCurrentPlatformSupported(),
          service: 'SubscriptionService'
        }
      );
      this.errorService.registerError(error);
      // Still mark as initialised to prevent blocking
      this.initialisedSubject.next(true);
    }
  }

  /**
   * Once RevenueCat has answered, its answer wins. While it is
   * silent, a token carrying free-trial or event.access grants
   * access straight away (HEC-377); a token with neither waits
   * out ACCESS_UNKNOWN_TIMEOUT_MS and then falls to the paywall.
   */
  private resolveAccess(
    subscribed: boolean | null,
    timedOut: boolean
  ): boolean | null {
    if (subscribed !== null) {
      return this.userContextService
        .hasEffectiveAccess(subscribed);
    }
    if (this.userContextService.hasRoleGrantedAccess()) {
      return true;
    }
    return timedOut ? false : null;
  }

  private startAccessUnknownTimer(): void {
    timer(SubscriptionService.ACCESS_UNKNOWN_TIMEOUT_MS)
      .subscribe(() => this.onAccessUnknownTimeout());
  }

  /**
   * Writes only the timed-out flag, never the status: a
   * late customerInfo can still resolve it, and the
   * paywall_presented capture waits for that resolution.
   * Fires for every user, role holder or not, so RevenueCat
   * slowness stays measurable in Sentry and PostHog even when
   * the roles mean nobody sees it (HEC-377).
   */
  private onAccessUnknownTimeout(): void {
    if (this.isSubscribedSubject.getValue() !== null
      || this.accessTimedOutSubject.getValue()) {
      return;
    }
    const timeoutMs =
      SubscriptionService.ACCESS_UNKNOWN_TIMEOUT_MS;
    const rolesGrantedAccess =
      this.userContextService.hasRoleGrantedAccess();
    const outcome = rolesGrantedAccess
      ? 'access held by token roles'
      : 'paywall shown on timeout';
    this.logger.warn(
      'Subscription status still unknown after '
      + `${timeoutMs}ms; ${outcome}`
    );
    this.observabilityService
      .trackSubscriptionDataAnomaly(
        'access_unresolved_timeout',
        'customerInfo did not arrive within the '
        + `access timeout; ${outcome}`,
        ObservabilityLevel.WARNING,
        {
          timeoutMs,
          rolesGrantedAccess,
          platform: Capacitor.getPlatform()
        }
      );
    // Platform is the app_platform super-property.
    this.postHogService.capture(
      'access_unresolved_timeout',
      {
        roles_granted_access: rolesGrantedAccess,
        timeout_ms: timeoutMs
      }
    );
    this.zone.run(
      () => this.accessTimedOutSubject.next(true)
    );
  }

  private async configurePurchases(
    appUserId: string
  ): Promise<void> {
    this.logger.info(
      '🚀 Starting RevenueCat configuration...'
    );

    await this.provider.setLogLevel('error');

    this.logger.debug('📝 Configuration details:', {
      platform: Capacitor.getPlatform(),
      appUserId: appUserId || 'none'
    });

    await this.provider.configure({
      appUserID: appUserId
    });

    this.logger.info(
      '✅ RevenueCat configuration completed'
    );

    this.provider.initialiseAttribution();

    await this.addCustomerInfoListener();
  }

  private async addCustomerInfoListener():
    Promise<void> {
    if (this.listenerRegistered) {
      this.logger.debug(
        'Customer info listener already '
        + 'registered, skipping'
      );
      return;
    }
    this.listenerRegistered = true;

    await this.provider
      .addCustomerInfoUpdateListener(
        async (customerInfo) => {
          try {
            const wasSubscribed =
              this.lastCustomerInfoSubscribed;
            this.customerInfo = customerInfo;

            this.logger.info(
              '🔔 Customer info update received: ',
              this.logger.stringify(customerInfo)
            );

            this.zone.run(() =>
              this.isSubscribedSubject.next(
                !!customerInfo
                  .entitlements.active['basic']
              )
            );
            this.lastCustomerInfoSubscribed =
              this.isSubscribed;

            // Log to Sentry for debugging
            // subscription status issues
            this.observabilityService
              .trackCustomerInfoReceived(
                this.loggedInUser?.getSub(),
                this.isSubscribed,
                {
                  entitlements: Object.keys(
                    customerInfo.entitlements.active
                  ),
                  allEntitlements: Object.keys(
                    customerInfo.entitlements.all
                  ),
                  originalPurchaseDate:
                    customerInfo.originalPurchaseDate,
                  latestExpirationDate:
                    customerInfo
                      .latestExpirationDate,
                  platform:
                    Capacitor.getPlatform()
                }
              );

            this.logger.info(
              'Subscription status: '
              + `${this.isSubscribed
                ? 'Subscribed'
                : 'Not Subscribed'}`
            );

            if (this.isSubscribed) {
              this.logger.info(
                '🔄 User subscribed, waiting for '
                + 'subscribed roles to load'
              );
              this.unsubscribePollPending = false;
              this.authService
                .waitForSubscribedRoles();
            } else {
              if (wasSubscribed) {
                this.unsubscribePollPending = true;
              }
              if (this.userContextService
                .isInFreeTrial()) {
                this.logger.info(
                  'User not subscribed but in '
                  + 'free trial, skipping role '
                  + 'polling'
                );
                this.unsubscribePollPending = false;
              } else {
                this.logger.info(
                  'User not subscribed and trial '
                  + 'expired, loading offerings'
                );
                // A background load must not own the
                // global spinner: getOfferings() has no
                // timeout (HEC-346). The paywall navigation
                // is the constructor's edge-triggered
                // subscription, not this listener.
                await this.loadOfferings(false);
                this.startUnsubscribedPollIfPending();
              }
            }

            this.logger.debug(
              '👤 Customer info updated:', {
                isSubscribed: this.isSubscribed,
                entitlements: Object.keys(
                  customerInfo.entitlements.active
                ),
                originalPurchaseDate:
                  customerInfo.originalPurchaseDate,
                latestExpirationDate:
                  customerInfo.latestExpirationDate
              }
            );
          } finally {
            this.initialisedSubject.next(true);
          }
        }
      );

    this.logger.info(
      '🎧 Customer info listener configured'
    );
  }

  /**
   * Runs after the offerings await, so it reads the newest
   * state rather than the stale view of the call that
   * started the load (HEC-376).
   */
  private startUnsubscribedPollIfPending(): void {
    if (this.isSubscribed) {
      this.logger.info(
        'Skipping unsubscribed role poll: a newer '
        + 'customerInfo reports subscribed'
      );
      return;
    }
    if (!this.unsubscribePollPending) {
      this.logger.info(
        'Skipping unsubscribed role poll: no in-session '
        + 'subscribed to not-subscribed transition pending'
      );
      return;
    }
    this.unsubscribePollPending = false;
    this.authService.waitForUnSubscribedRoles();
  }

  /**
   * @param showSpinner false on the customerInfo listener path,
   * where the load is a background one; the pricing page, the
   * paywall retry and initialiseUser() keep the spinner.
   */
  public async loadOfferings(
    showSpinner: boolean = true
  ): Promise<void> {
    this.logger.info(
      '🔄 Starting to load offerings...'
    );
    if (showSpinner) {
      this.spinnerService.showImmediately();
    }
    try {
      // Check configuration before loading
      const isConfigured =
        await this.provider.isConfigured();
      this.logger.info(
        `📋 Configuration check: ${isConfigured}`
      );

      if (!isConfigured) {
        throw new Error(
          'Purchases not configured when '
          + 'attempting to load offerings'
        );
      }

      const packages =
        await this.provider.getOfferings();

      this.logger.info(
        '✅ Successfully loaded '
        + `${packages.length} offerings`
      );

      // Log offering details for debugging
      packages.forEach((pkg, index) => {
        const product = pkg.product;
        const hasFreeTrial =
          product.introPrice?.price === 0;
        const trialPeriod =
          product.introPrice?.period ?? 'none';

        this.logger.debug(
          `📦 Package ${index + 1}: `
          + pkg.identifier,
          {
            productId: product.identifier,
            price: product.priceString,
            monthlyPrice:
              product.pricePerMonthString,
            hasFreeTrial,
            trialPeriod,
            subscriptionPeriod:
              product.subscriptionPeriod
          }
        );
      });

      if (packages.length > 0) {
        this.observabilityService
          .trackSubscriptionsOffered(packages, {
            platform: Capacitor.getPlatform(),
            offeringCount: packages.length,
            userId: this.loggedInUser?.getSub(),
            source: 'loadOfferings'
          });
      } else {
        this.logger.warn(
          '⚠️ No packages available '
          + '- check RevenueCat configuration'
        );
        this.observabilityService
          .trackSubscriptionDataAnomaly(
            'no_offerings_available',
            'Subscription component initialized '
            + 'with no offerings',
            ObservabilityLevel.WARNING,
            { offeringsCount: 0 }
          );
      }

      this.offeringsSubject.next(packages);
    } catch (error) {
      this.logger.error(
        '❌ Error loading offerings:',
        this.logger.stringify(error)
      );
      this.observabilityService.captureException(
        error,
        {
          context: 'load_offerings',
          platform: Capacitor.getPlatform(),
          userId: this.loggedInUser?.getSub(),
          service: 'SubscriptionService',
          error: error instanceof Error
            ? error.message : 'Unknown error'
        }
      );
      this.errorService.registerError(error);
      // Emit empty array on error
      this.offeringsSubject.next([]);
      this.initialisedSubject.next(true);
    } finally {
      if (showSpinner) {
        this.spinnerService.hide();
      }
      this.logger.info(
        '🔚 Finished loading offerings'
      );
    }
  }

  /**
   * When a pair of access readings means "take the user to the
   * paywall" (HEC-346). Two conditions, both requiring access to
   * now be false:
   *
   * - settled no access: RevenueCat has actually answered
   *   (`sub !== null`) and the previous reading was not already a
   *   settled no-access. A plain "any edge into false" would also
   *   fire on the HEC-359 ACCESS_UNKNOWN_TIMEOUT_MS drop from null
   *   while RevenueCat is still silent, pulling a paying subscriber
   *   who reloaded on /tabs/learners behind a slow answer (and
   *   holds every Keycloak role) onto Home.
   * - lost access: the previous reading had access, whatever
   *   RevenueCat's state - the token lost a role on refresh.
   *
   * A "true-to-false only" rule would be wrong too: no route other
   * than Home checks access (tabs.routes' isLoggedIn guard only
   * checks that a signed-in user exists), so a lapsed user who
   * reloads on /tabs/learners relies on the resolution into
   * no-access to reach the paywall, as the old listener navigation
   * gave them.
   *
   * Walking the cases: lapsed user, fast answer, {null,null} to
   * {false,false} navigates once; lapsed user, slow answer,
   * {null,null} to {false,null} is skipped and the later
   * {false,null} to {false,false} navigates; subscriber, slow
   * answer, {false,null} then {true,true} never navigates; a
   * RevenueCat unsubscribe, {true,true} to {false,false}, meets
   * both conditions in one emission, so one navigation; a trial
   * lost on refresh, {true,x} to {false,x}, navigates through
   * lost access; a repeated false re-fires nothing.
   *
   * Since HEC-377 a token carrying event.access holds access
   * while RevenueCat is silent: {true,null} then {true,true}
   * never navigates, and {true,null} to {false,false} navigates
   * once through lost access. Both readings come from
   * accessReadings$, one source, so no transient {false,null}
   * appears between them.
   */
  private static shouldNavigateToPaywall(
    previous: AccessReading,
    current: AccessReading
  ): boolean {
    if (current.access !== false) {
      return false;
    }
    const previouslySettledNoAccess =
      previous.access === false && previous.sub !== null;
    const settledNoAccess =
      current.sub !== null && !previouslySettledNoAccess;
    const lostAccess = previous.access === true;
    return settledNoAccess || lostAccess;
  }

  /**
   * Returns early on the logout transition (hasAccess$ drops
   * when currentUser$ goes null, and the login page is where
   * the user is headed anyway). On a busy route the navigation
   * is deferred, not dropped (see paywallDeferred).
   */
  private navigateToPaywallUnlessBusy(): void {
    if (!this.userContextService.getCurrentUser()) {
      return;
    }
    const url = this.router.url;
    if (SubscriptionService.isBusyRoute(url)) {
      this.paywallDeferred = true;
      this.logger.info(
        'Access lost on a busy route, deferring navigation:', url
      );
      return;
    }
    this.paywallDeferred = false;
    this.logger.info(
      'Access lost, navigating to the home paywall from:', url
    );
    this.router.navigate(['tabs', 'home']).then();
  }

  /**
   * Runs a navigation deferred on a busy route once the user
   * has left it, provided they are still signed in and access
   * is still lost (paywallDeferred is cleared the moment access
   * returns). Landing on Home itself needs no navigation: the
   * paywall renders there. Found in test on HEC-346: a draft
   * resumed on cold start parked a lapsed user on the form, and
   * cancelling it left them on an empty tab page.
   */
  private runDeferredPaywallNavigation(url: string): void {
    if (!this.paywallDeferred) {
      return;
    }
    if (!this.userContextService.getCurrentUser()) {
      this.paywallDeferred = false;
      return;
    }
    if (SubscriptionService.isBusyRoute(url)) {
      return;
    }
    this.paywallDeferred = false;
    if (SubscriptionService.isHomeUrl(url)) {
      return;
    }
    this.logger.info(
      'Left a busy route with access still lost, '
      + 'navigating to the home paywall from:', url
    );
    this.router.navigate(['tabs', 'home']).then();
  }

  /**
   * Home itself, where the paywall renders; not its sub-pages
   * (the Activity Summary under /tabs/home/summary), which the
   * Home tab restores to and which have no paywall of their own.
   */
  private static isHomeUrl(url: string): boolean {
    return url.split(/[?#]/)[0] === '/tabs/home';
  }

  private static isBusyRoute(url: string): boolean {
    return SubscriptionService.BUSY_ROUTE_PREFIXES
      .some(prefix => url.startsWith(prefix));
  }

  private readonly supportedPlatforms =
    ['ios', 'android', 'web'];

  private isCurrentPlatformSupported(): boolean {
    return this.supportedPlatforms.includes(
      Capacitor.getPlatform()
    );
  }

  isManagementAvailable(): boolean {
    return this.provider.isManagementAvailable()
      && this.isSubscribed;
  }

  async openManagementPage(): Promise<void> {
    await this.provider.openManagementPage();
  }

  isRedeemCodeAvailable(): boolean {
    return this.provider.isRedeemCodeAvailable();
  }

  async redeemCode(): Promise<void> {
    this.observabilityService
      .trackRedeemCodeOpened({
        userId: this.loggedInUser?.getSub(),
        platform: Capacitor.getPlatform()
      });
    try {
      await this.provider.redeemCode();
    } catch (error: any) {
      this.logger.error(
        'Error opening redeem code flow:',
        this.logger.stringify(error)
      );
      this.observabilityService
        .trackRedeemCodeFailed(
          error instanceof Error
            ? error.message : 'Unknown error',
          {
            userId: this.loggedInUser?.getSub(),
            platform: Capacitor.getPlatform()
          }
        );
      throw error;
    }
  }

  private async arePurchasesReady(): Promise<boolean> {
    try {
      return await this.provider.isConfigured();
    } catch (error) {
      this.logger.error(
        'Error checking Purchases configuration:',
        error
      );
      return false;
    }
  }

  public async buy(
    offering: SubscriptionProduct
  ) {
    try {
      this.spinnerService.showImmediately();

      const offer = offering.effectiveOffer;

      // Track purchase attempt
      this.observabilityService
        .trackPurchaseAttempted(
          offering.identifier,
          {
            platform: Capacitor.getPlatform(),
            productType: offering.productType,
            price: offering.price,
            priceString: offering.priceString,
            currencyCode: offering.currencyCode,
            userId: this.loggedInUser?.getSub(),
            source: 'buy_method',
            offering_id: offering.offeringIdentifier,
            offer_kind: offer?.kind,
            offer_price: offer?.priceString,
            intro_eligibility: offer?.eligibility
          }
        );

      const purchasesReady =
        await this.arePurchasesReady();
      if (purchasesReady) {
        this.logger.debug(
          'Buying offering:',
          this.logger.stringify(offering)
        );
        const email =
          this.loggedInUser?.userInfo?.['email'];
        const purchaseResult =
          await this.provider.purchase(
            offering,
            {
              customerEmail: email,
              appUserID: this.loggedInUser?.getSub()
            }
          );

        // Track successful purchase
        if (typeof purchaseResult
          .customerInfo.entitlements
          .active['basic'] !== 'undefined') {

          this.postHogService.capture(
            'user_subscribed', {
              product: offering.identifier,
              price: offering.price,
              currency: offering.currencyCode,
              offering_id: offering.offeringIdentifier,
              offer_kind: offer?.kind,
              offer_price: offer?.priceString,
              intro_eligibility: offer?.eligibility
            }
          );
          this.observabilityService
            .trackPurchaseSuccess(
              offering.identifier,
              purchaseResult
                .customerInfo
                .originalPurchaseDate ?? undefined,
              {
                platform:
                  Capacitor.getPlatform(),
                productType: offering.productType,
                price: offering.price,
                priceString: offering.priceString,
                currencyCode:
                  offering.currencyCode,
                userId:
                  this.loggedInUser?.getSub(),
                entitlements: Object.keys(
                  purchaseResult
                    .customerInfo
                    .entitlements.active
                ),
                source: 'buy_method',
                offering_id: offering.offeringIdentifier,
                offer_kind: offer?.kind,
                offer_price: offer?.priceString,
                intro_eligibility: offer?.eligibility
              }
            );
        }
      }
    } catch (error: any) {
      // Detect and track user cancellations
      // separately from failures.
      // Web SDK: errorCode === 1
      // Capacitor: message contains 'cancelled'
      const isUserCancellation =
        error
        && typeof error === 'object'
        && (error.errorCode === 1
          || error?.message?.includes('cancelled'));
      if (isUserCancellation) {
        // Track user cancellation
        this.observabilityService
          .trackPurchaseCancelled(
            offering.identifier,
            {
              platform: Capacitor.getPlatform(),
              productType: offering.productType,
              price: offering.price,
              priceString: offering.priceString,
              currencyCode: offering.currencyCode,
              userId:
                this.loggedInUser?.getSub(),
              source: 'buy_method'
            }
          );
        this.logger.info(
          'Purchase cancelled by user:',
          offering.identifier
        );
        return;
      }

      // Track actual purchase failures
      this.observabilityService
        .trackPurchaseFailed(
          offering.identifier,
          error.message
            ?? 'Unknown purchase error',
          {
            platform: Capacitor.getPlatform(),
            productType: offering.productType,
            price: offering.price,
            priceString: offering.priceString,
            currencyCode: offering.currencyCode,
            userId: this.loggedInUser?.getSub(),
            errorCode: error.code,
            source: 'buy_method'
          }
        );

      this.logger.error(
        'Error buying offering:',
        this.logger.stringify(error)
      );
      this.errorService.registerError(error, true);
    } finally {
      this.spinnerService.hide();
    }
  }

  public async restorePurchases(): Promise<boolean> {
    this.logger.info(
      '🔄 Starting restore purchases...'
    );
    this.spinnerService.showImmediately();

    this.observabilityService
      .trackRestorePurchasesAttempted({
        userId: this.loggedInUser?.getSub(),
        platform: Capacitor.getPlatform()
      });

    try {
      const purchasesReady =
        await this.arePurchasesReady();
      if (!purchasesReady) {
        throw new Error(
          'Purchases not configured when '
          + 'attempting to restore'
        );
      }

      const result =
        await this.provider.restorePurchases();
      const isSubscribed =
        !!result.customerInfo
          .entitlements.active['basic'];
      this.zone.run(
        () => this.isSubscribedSubject
          .next(isSubscribed)
      );

      this.logger.info(
        '✅ Restore purchases completed '
        + `- Subscribed: ${isSubscribed}`
      );

      this.observabilityService
        .trackRestorePurchasesSuccess({
          userId: this.loggedInUser?.getSub(),
          isSubscribed,
          entitlements: Object.keys(
            result.customerInfo
              .entitlements.active
          ),
          platform: Capacitor.getPlatform()
        });

      return isSubscribed;
    } catch (error: any) {
      this.logger.error(
        '❌ Error restoring purchases:',
        this.logger.stringify(error)
      );

      this.observabilityService
        .trackRestorePurchasesFailed(
          error instanceof Error
            ? error.message : 'Unknown error',
          {
            userId: this.loggedInUser?.getSub(),
            platform: Capacitor.getPlatform(),
            errorCode: error?.code
          }
        );

      this.errorService.registerError(error, true);
      return false;
    } finally {
      this.spinnerService.hide();
    }
  }
}
